import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe, type StripeElementsOptions } from '@stripe/stripe-js';
import { envConfig } from '../env';

const stripePk = envConfig.stripePublishableKey.trim();
export const stripePromise = stripePk ? loadStripe(stripePk) : null;

/** Embedded Stripe Payments UI — submits confirmation after server creates PI + CRM row. */

function CheckoutForm({ onComplete }: { onComplete: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const { error: submitError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/dashboard`,
      },
      redirect: 'if_required',
    });
    setSubmitting(false);
    if (submitError) {
      setError(submitError.message ?? 'Payment failed');
      return;
    }
    onComplete();
  }

  return (
    <form className="space-y-4" onSubmit={onSubmit}>
      <PaymentElement />
      {error ? <p className="text-sm text-red-400">{error}</p> : null}
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full px-5 py-2.5 rounded-full bg-blue-600 hover:bg-blue-500 text-sm font-medium disabled:opacity-50"
      >
        {submitting ? 'Processing…' : 'Pay & subscribe'}
      </button>
    </form>
  );
}

export function InvestCheckout({
  clientSecret,
  onComplete,
}: {
  clientSecret: string;
  onComplete: () => void;
}) {
  const options = useMemo<StripeElementsOptions>(
    () => ({
      clientSecret,
      appearance: { theme: 'night' },
    }),
    [clientSecret]
  );

  if (!stripePromise) {
    return (
      <p className="text-sm text-amber-300">
        Configure <code className="text-xs">VITE_STRIPE_PUBLISHABLE_KEY</code> to enable embedded payments.
      </p>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm onComplete={onComplete} />
    </Elements>
  );
}
